/**
 * Instance resolver — DEC-0409 step 1 (docs/specs/universal-instance-contract.md).
 *
 * One pure function answers the three questions every lens asks about an
 * instance: who is it (identity), where does its data live (source), and
 * which fields does it carry (system strip + effective fields).
 *
 * Pure and deterministic: no network, no database. Callers fetch the inputs
 * with the existing queries (fetchNote, fetchEntityTypes, fetchGlobalFields,
 * fetchEffectiveFieldRefs) and pass them in, so every lens that resolves the
 * same inputs gets the same answer.
 *
 * The effective-field rule is lifted verbatim from NoteMeta (the most complete
 * reader today) so adopting the resolver changes nothing on screen:
 *   effective refs (v_entity_type_effective_fields, falling back to the flat
 *   field_refs when the view returned nothing) ∪ UNIVERSAL_FIELD_KEYS,
 *   minus !isFieldRenderable, universal first, global_fields order otherwise,
 *   then narrowed by stakeholder visible_fields when one applies.
 *
 * System fields come from the root entity type (`universal_base`, DEC-0023)
 * that every non-library type inherits through parent_id — the view reports
 * them with from_layer = the root slug, and they surface here as origin 'root'.
 */

import type { EntityType, GlobalField, I18nLabel, NoteRecord } from './types';
import type { EntitySource, EntitySourceOrigin } from '@/lib/internal-tools/entitySourceResolver';
import { SYSTEM_FIELDS, UNIVERSAL_FIELD_KEYS, isFieldRenderable } from './builtinFields';
import {
  describeInstanceViewKeys,
  instanceViewOrigin,
  resolveInstanceViewKeys,
  type ResolvedInstanceView,
} from './viewsRegistry';
import { resolveInstanceTemplate, type ResolvedTemplate } from './templateRegistry';

/** The tree root every entity type inherits from (migration 20260830141000). */
export const ROOT_ENTITY_TYPE_SLUG = 'universal_base';

/** One row of v_entity_type_effective_fields, as fetchEffectiveFieldRefs returns it. */
export interface EffectiveFieldRef {
  ref: string;
  from_layer: string;
  depth: number;
}

/** Why a field is on this instance. */
export type FieldOrigin =
  /** Claimed by the type's own field_refs (depth 0). */
  | 'direct'
  /** Inherited from the root entity type (DEC-0023). */
  | 'root'
  /** Inherited from an intermediate ancestor library (DEC-0017). */
  | 'inherited'
  /** Not claimed by the type at all — carried by UNIVERSAL_FIELD_KEYS. */
  | 'universal';

export interface ResolvedField {
  metaKey: string;
  field: GlobalField;
  origin: FieldOrigin;
  /** Layer the ref came from; null for universal-only and fallback refs. */
  fromLayer: string | null;
  depth: number | null;
  value: unknown;
}

/** A read-only, column-backed field of the system strip (title, status, …). */
export interface ResolvedSystemField {
  key: string;
  noteField: string;
  label: I18nLabel;
  icon: string;
  value: unknown;
}

export interface InstanceIdentity {
  id: string;
  title: string;
  /** Canonical FK to entity_types.id when known. */
  entityTypeId: string | null;
  entitySlug: string | null;
  /**
   * How the type was matched: 'id' when vb_records.entity_type_id agrees with
   * the type row, 'slug' when only the text column / route slug was available,
   * 'none' when no type row was supplied.
   */
  typeResolvedBy: 'id' | 'slug' | 'none';
  /** True when the row's entity_type_id points at a different type than supplied. */
  typeConflict: boolean;
  recordType: string | null;
  status: string;
}

export interface InstanceSource {
  /** Contract §2: local = vb_records, mirror = external and read-only. */
  kind: 'local' | 'mirror';
  origin: EntitySourceOrigin | 'vb_records';
  table: string;
  writable: boolean;
  externalSource: string | null;
  externalId: string | null;
}

export interface ResolvedInstance {
  identity: InstanceIdentity;
  source: InstanceSource;
  fields: {
    system: ResolvedSystemField[];
    effective: ResolvedField[];
  };
  /** DEC-0409 step 2: the ordered views this instance opens with (viewsRegistry). */
  views: ResolvedInstanceView[];
  /**
   * DEC-0409 step 3: the definition this instance renders with, where it came
   * from, and whether a newer published version is waiting. A newer version is
   * reported, never adopted — adopting is a person's decision on a review
   * screen. The registry itself is templateRegistry.ts.
   */
  template: ResolvedTemplate;
}

export interface ResolveInstanceInput {
  note: NoteRecord;
  entityType: EntityType | null;
  globalFields: GlobalField[];
  /** Rows from fetchEffectiveFieldRefs; null/empty falls back to entityType.field_refs. */
  effectiveRefs?: EffectiveFieldRef[] | null;
  /** resolveEntitySource(slug) for the route slug; null = plain vb_records. */
  entitySource?: EntitySource | null;
  /** Stakeholder visible_fields; applied only when non-empty (NoteMeta RBAC rule). */
  visibleFieldKeys?: ReadonlySet<string> | null;
}

export function resolveInstanceSource(
  note: NoteRecord,
  entitySource: EntitySource | null | undefined,
): InstanceSource {
  // Same branches, same order as fetchNote — the resolver must name the table
  // the row was actually read from, never a guess.
  if (entitySource?.origin === 'fireberry-mirror' && entitySource.table) {
    const fireberryId = note.meta?.fireberry_id;
    return {
      kind: 'mirror', origin: 'fireberry-mirror', table: entitySource.table, writable: false,
      externalSource: 'fireberry',
      externalId: typeof fireberryId === 'string' ? fireberryId : note.id,
    };
  }
  if (entitySource?.origin === 'marketplace' && entitySource.table && entitySource.table !== 'vb_records') {
    return {
      kind: 'mirror', origin: 'marketplace', table: entitySource.table, writable: false,
      externalSource: entitySource.table, externalId: note.id,
    };
  }
  return {
    kind: 'local', origin: 'vb_records', table: 'vb_records', writable: true,
    externalSource: null, externalId: null,
  };
}

function resolveIdentity(note: NoteRecord, entityType: EntityType | null): InstanceIdentity {
  // entity_type_id is not on NoteRecord yet (DEC-0081 added the column; the
  // TS shape still lags), so read it defensively rather than widen the type.
  const rowTypeId = (note as NoteRecord & { entity_type_id?: string | null }).entity_type_id ?? null;
  const typeConflict = Boolean(rowTypeId && entityType && entityType.id !== rowTypeId);
  const typeResolvedBy: InstanceIdentity['typeResolvedBy'] = !entityType
    ? 'none'
    : rowTypeId && entityType.id === rowTypeId ? 'id' : 'slug';
  return {
    id: note.id,
    title: note.title,
    entityTypeId: rowTypeId ?? entityType?.id ?? null,
    entitySlug: entityType?.slug ?? note.entity_type ?? null,
    typeResolvedBy,
    typeConflict,
    recordType: note.record_type ?? null,
    status: note.status,
  };
}

export function resolveSystemFields(note: NoteRecord): ResolvedSystemField[] {
  return SYSTEM_FIELDS.map(sf => ({
    key: sf.key,
    noteField: sf.noteField,
    label: sf.label,
    icon: sf.icon,
    value: note[sf.noteField as keyof NoteRecord],
  }));
}

export function resolveEffectiveFields(
  /** Only meta is read — a lens without a row (the list page) passes { meta: {} }. */
  note: Pick<NoteRecord, 'meta'>,
  entityType: EntityType | null,
  globalFields: GlobalField[],
  effectiveRefs?: EffectiveFieldRef[] | null,
  visibleFieldKeys?: ReadonlySet<string> | null,
): ResolvedField[] {
  // NoteMeta renders no field grid without a type row; neither do we.
  if (!entityType) return [];

  // First occurrence wins: the view orders by depth, so a type's own claim
  // (depth 0) beats the same key inherited from further up.
  const refInfo = new Map<string, { fromLayer: string | null; depth: number | null }>();
  if (effectiveRefs && effectiveRefs.length > 0) {
    for (const r of effectiveRefs) {
      if (!refInfo.has(r.ref)) refInfo.set(r.ref, { fromLayer: r.from_layer, depth: r.depth });
    }
  } else {
    for (const ref of entityType.field_refs ?? []) {
      if (!refInfo.has(ref)) refInfo.set(ref, { fromLayer: entityType.slug, depth: 0 });
    }
  }

  const universal = new Set<string>(UNIVERSAL_FIELD_KEYS);
  const resolved = globalFields
    .filter(f => refInfo.has(f.meta_key) || universal.has(f.meta_key))
    .filter(isFieldRenderable)
    .map((f): ResolvedField => {
      const info = refInfo.get(f.meta_key);
      let origin: FieldOrigin;
      if (!info) origin = 'universal';
      else if (!info.depth) origin = 'direct';
      else if (info.fromLayer === ROOT_ENTITY_TYPE_SLUG) origin = 'root';
      else origin = 'inherited';
      return {
        metaKey: f.meta_key,
        field: f,
        origin,
        fromLayer: info?.fromLayer ?? null,
        depth: info?.depth ?? null,
        value: note.meta?.[f.meta_key],
      };
    });

  // Stable sort: universal keys first, global_fields order preserved within each group.
  resolved.sort((a, b) => (universal.has(a.metaKey) ? 0 : 1) - (universal.has(b.metaKey) ? 0 : 1));

  if (visibleFieldKeys && visibleFieldKeys.size > 0) {
    return resolved.filter(r => visibleFieldKeys.has(r.metaKey));
  }
  return resolved;
}

/**
 * Column set for a type's list page — the same effective fields every instance
 * of the type shows, so list and instance page agree (contract gap #1).
 *
 * A type that claims nothing of its own (only root/universal fields — an import
 * that never filled field_refs) keeps the deriveFieldsFromRecords floor: the
 * keys its records actually carry are appended after the resolved fields.
 */
export function resolveListFields(
  entityType: EntityType | null,
  globalFields: GlobalField[],
  effectiveRefs: EffectiveFieldRef[] | null | undefined,
  derive: () => GlobalField[],
): GlobalField[] {
  const resolved = resolveEffectiveFields({ meta: {} }, entityType, globalFields, effectiveRefs);
  const claimsOwn = resolved.some(r => r.origin === 'direct' || r.origin === 'inherited');
  const fields = resolved.map(r => r.field);
  if (claimsOwn) return fields;
  const seen = new Set(fields.map(f => f.meta_key));
  return [...fields, ...derive().filter(f => !seen.has(f.meta_key))];
}

export function resolveInstance(input: ResolveInstanceInput): ResolvedInstance {
  const { note, entityType, globalFields, effectiveRefs, entitySource, visibleFieldKeys } = input;
  // The template is resolved here so a caller reads one object, but views keep
  // coming from the views registry: templateRegistry answers "which definition",
  // viewsRegistry answers "which tabs". Rows the resolver cannot fetch (a linked
  // template, a pinned version) are absent by design — it is pure, and with no
  // link the answer is the type's own template_config, which is what every
  // instance resolves to today.
  const template = resolveInstanceTemplate({ entityType, meta: note?.meta ?? null });
  return {
    identity: resolveIdentity(note, entityType),
    source: resolveInstanceSource(note, entitySource),
    fields: {
      system: resolveSystemFields(note),
      effective: resolveEffectiveFields(note, entityType, globalFields, effectiveRefs, visibleFieldKeys),
    },
    views: describeInstanceViewKeys(resolveInstanceViewKeys(entityType), instanceViewOrigin(entityType)),
    template,
  };
}
